import { z } from 'zod'
import { DocketRecordSchema } from '@/agent-v2/references/docket-record'

export enum ColumnKind {
  Text = 'text',
  Number = 'number',
  Date = 'date',
  Tags = 'tags',
}
export enum FilterOperator {
  Contains = 'contains',
  Equals = 'equals',
  Greater = 'gt',
  AtLeast = 'gte',
  Less = 'lt',
  AtMost = 'lte',
  IsBlank = 'is_blank',
  IsNotBlank = 'is_not_blank',
}
export const TableSchema = z.object({
  id: z.string().uuid(),
  matter_id: z.string().uuid(),
  name: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }).nullable(),
  created_by_user_id: z.string(),
  document_analysis: z.boolean(),
})
export const ColumnTagSchema = z.object({
  label: z.string().min(1),
  color: z.enum(['Black', 'Blue', 'Green', 'Yellow', 'Orange', 'Red', 'Neutral']),
  custom_color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullable(),
})
export type ColumnTag = z.infer<typeof ColumnTagSchema>

export const ColumnSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  question: z.string(),
  question_revision: z.number().int().nonnegative(),
  manual_entry: z.boolean(),
  source_file: z.boolean().optional(),
  pending_analysis: z.boolean(),
  analysis_running: z.boolean(),
  kind: z.nativeEnum(ColumnKind),
  position: z.number().int(),
  tags: z.array(ColumnTagSchema),
  allow_multiple: z.boolean(),
})
export const CellReferenceSchema = z.object({
  web_url: z.string().url().nullish(),
  id: z.string().uuid(),
  document_id: z.string().uuid().nullable(),
  agency_table_id: z.string().uuid().nullable().optional(),
  docket_table_id: z.string().uuid().nullish(),
  docket: DocketRecordSchema.nullish(),
  support_summary: z.string().nullable().optional(),
  legal_kind: z.enum(['caselaw', 'lrr']).nullable().optional(),
  legal_path: z.string().nullable().optional(),
  document_name: z.string(),
  page_number: z.number().int().positive().nullable(),
  excerpt: z.string().nullable(),
  anchor_text: z.string().nullable(),
})
export type CellReference = z.infer<typeof CellReferenceSchema>
export const CellSegmentSchema = z.object({
  text: z.string(),
  reference_ids: z.array(z.string().uuid()),
})
export type CellSegment = z.infer<typeof CellSegmentSchema>
export const CellSchema = z.object({
  column_id: z.string().uuid(),
  segments: z.array(CellSegmentSchema),
  revision: z.string().uuid().nullable(),
  value: z.string(),
  search_offsets: z.array(z.number().int().nonnegative()).optional(),
  original_value: z.string().nullable(),
  needs_verification: z.boolean().optional(),
  edited_at: z.string().nullable(),
  edited_by: z.string().nullable(),
  references: z.array(CellReferenceSchema).optional(),
})
export const RowSchema = z.object({
  id: z.string().uuid(),
  cells: z.array(CellSchema),
  source_filename: z.string().nullish(),
  search_filename: z.string().nullable().optional(),
  filename_search_offsets: z.array(z.number().int().nonnegative()).optional(),
})
export const TablePageSchema = z.object({
  capacity: z
    .object({ row_count: z.number().int().nonnegative(), column_count: z.number().int().nonnegative(), max_cells: z.number().int().positive() })
    .optional(),
  table: TableSchema,
  columns: z.array(ColumnSchema),
  rows: z.array(RowSchema),
  total: z.number().int().nonnegative().nullable(),
  has_more: z.boolean().optional(),
  document_count: z.number().int().nonnegative(),
  document_based: z.boolean(),
})
export type MatterTable = z.infer<typeof TableSchema>
export type TableColumn = z.infer<typeof ColumnSchema>
export type TableCell = z.infer<typeof CellSchema>
export type TableRow = z.infer<typeof RowSchema>
export type TablePage = z.infer<typeof TablePageSchema>
export const ColumnFiltersSchema = z.array(
  z.object({ column_id: z.string(), operator: z.nativeEnum(FilterOperator), value: z.union([z.string(), z.array(z.string())]) })
)
export type ColumnFilter = z.infer<typeof ColumnFiltersSchema>[number]
export enum FilterMatch {
  All = 'all',
  Any = 'any',
}

export const TableSearchCountsSchema = z.object({
  total_rows: z.number().int().nonnegative(),
  total_matches: z.number().int().nonnegative(),
  page_match_offsets: z.array(z.number().int().nonnegative()),
})
export type TableSearchCounts = z.infer<typeof TableSearchCountsSchema>

export type TableQuery = {
  include_total?: boolean
  search?: string
  search_column_ids?: string[]
  search_filenames?: boolean
  filter_match?: FilterMatch
  filters: ColumnFilter[]
  sorts: { column_id: string; descending: boolean }[]
  offset: number
  limit: number
}
export type TableEdit =
  | { action: 'rename-table'; name: string }
  | { action: 'add-analysis-column'; question: string; kind: ColumnKind; tags?: ColumnTag[]; allow_multiple?: boolean }
  | { action: 'add-column'; name: string; kind: ColumnKind; question?: string; manual_entry?: boolean; tags?: ColumnTag[]; allow_multiple?: boolean }
  | {
      action: 'edit-column'
      column_id: string
      name: string
      question: string
      manual_entry?: boolean
      kind?: ColumnKind
      tags?: ColumnTag[]
      expected_tags?: ColumnTag[]
      allow_multiple?: boolean
    }
  | { action: 'delete-column'; column_id: string }
  | { action: 'add-row' }
  | { action: 'delete-row'; row_id: string }
  | { action: 'delete-rows'; row_ids: string[] }
  | {
      action: 'edit-cell'
      row_id: string
      column_id: string
      value: string | string[]
      expected_value: string
      expected_revision?: string | null
      expected_question_revision?: number | null
      new_tags?: ColumnTag[]
      clear_verification?: boolean
      content?: CellSegment[]
      reference_ids_to_remove?: string[]
    }

export type TableEditResult = { ok: true } | { ok: false; error: string; conflict: boolean }
